import React, { useEffect, useState } from 'react';
import { getJornadas, createJornada, updateJornada, deleteJornada } from '../services/api';

const DIAS = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'];
const DIAS_FULL = {
  SEG: 'Segunda-feira', TER: 'Terça-feira', QUA: 'Quarta-feira',
  QUI: 'Quinta-feira', SEX: 'Sexta-feira', SAB: 'Sábado', DOM: 'Domingo',
};
const DIAS_SHORT = { SEG: 'Seg', TER: 'Ter', QUA: 'Qua', QUI: 'Qui', SEX: 'Sex', SAB: 'Sáb', DOM: 'Dom' };

const DIA_VAZIO = { entrada: '', inicio_almoco: '', fim_almoco: '', saida: '' };

const toMin = t => { if (!t) return null; const [hh, mm] = t.split(':').map(Number); return hh * 60 + mm; };

const calcCarga = h => {
  const e = toMin(h.entrada);
  if (!e) return 0;
  const s = toMin(h.saida);
  const ia = toMin(h.inicio_almoco);
  if (s && s > e) {
    let total = s - e;
    const fa = toMin(h.fim_almoco);
    if (ia && fa && fa > ia) total -= (fa - ia);
    return total / 60;
  }
  // Sem saída mas com início do almoço: meia jornada (entrada → almoço)
  if (ia && ia > e) return (ia - e) / 60;
  return 0;
};

const toHHMM = horas => {
  if (!horas) return '—';
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const FORM0 = {
  nome: '', ativo: true,
  carga_horaria: '8.00', vigencia_inicio: '', vigencia_fim: '',
  horarios_dias: {},
};

export default function Jornadas() {
  const [jornadas, setJornadas] = useState([]);
  const [filtros, setFiltros] = useState({ ativo: 'true' });
  const [form, setForm] = useState(FORM0);
  const [editId, setEditId] = useState(null);
  const [modal, setModal] = useState(false);
  const [consulta, setConsulta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => { carregar(); }, [filtros]); // eslint-disable-line

  const carregar = async () => {
    setLoading(true);
    try { const r = await getJornadas(filtros); setJornadas(r.data.results || r.data); }
    finally { setLoading(false); }
  };

  // ── form helpers ──────────────────────────────────────
  const isDiaAtivo = dia => !!form.horarios_dias[dia];

  const toggleDia = dia => {
    const hd = { ...form.horarios_dias };
    if (hd[dia]) { delete hd[dia]; }
    else { hd[dia] = { ...DIA_VAZIO }; }
    setForm({ ...form, horarios_dias: hd, carga_horaria: mediaCarga(hd) });
  };

  const setHorarioDia = (dia, campo, valor) => {
    const hd = { ...form.horarios_dias, [dia]: { ...form.horarios_dias[dia], [campo]: valor } };
    setForm({ ...form, horarios_dias: hd, carga_horaria: mediaCarga(hd) });
  };

  const mediaCarga = hd => {
    const dias = Object.values(hd);
    if (!dias.length) return '0.00';
    const total = dias.reduce((s, d) => s + calcCarga(d), 0);
    return (total / dias.length).toFixed(2);
  };

  const copiarPrimeiro = () => {
    const ativos = DIAS.filter(d => form.horarios_dias[d]);
    if (ativos.length < 2) return;
    const modelo = form.horarios_dias[ativos[0]];
    const hd = {};
    ativos.forEach(d => { hd[d] = { ...modelo }; });
    setForm({ ...form, horarios_dias: hd, carga_horaria: mediaCarga(hd) });
  };

  // ── abrir / salvar / excluir ──────────────────────────
  const copiar = (j) => {
    const hd = j.horarios_dias && Object.keys(j.horarios_dias).length ? j.horarios_dias : {};
    setForm({
      nome: `Cópia de ${j.nome}`,
      ativo: j.ativo,
      carga_horaria: j.carga_horaria,
      vigencia_inicio: j.vigencia_inicio || '',
      vigencia_fim: j.vigencia_fim || '',
      horarios_dias: hd,
    });
    setEditId(null);
    setErro(''); setModal(true);
  };

  const abrir = (j = null) => {
    if (j) {
      const hd = j.horarios_dias && Object.keys(j.horarios_dias).length
        ? j.horarios_dias
        : {};
      setForm({
        nome: j.nome || '',
        ativo: j.ativo,
        carga_horaria: j.carga_horaria,
        vigencia_inicio: j.vigencia_inicio || '',
        vigencia_fim: j.vigencia_fim || '',
        horarios_dias: hd,
      });
      setEditId(j.id);
    } else {
      setForm(FORM0);
      setEditId(null);
    }
    setErro(''); setModal(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (!Object.keys(form.horarios_dias).length) {
      setErro('Selecione pelo menos um dia da semana.'); return;
    }
    setLoading(true);
    try {
      const d = {
        ...form,
        dias_semana: Object.keys(form.horarios_dias),
        hora_entrada: null, hora_saida: null,
        hora_inicio_almoco: null, hora_fim_almoco: null,
        vigencia_inicio: form.vigencia_inicio || null,
        vigencia_fim: form.vigencia_fim || null,
      };
      editId ? await updateJornada(editId, d) : await createJornada(d);
      setModal(false); carregar();
    } catch (err) {
      setErro(JSON.stringify(err.response?.data || 'Erro ao salvar'));
    } finally { setLoading(false); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta jornada?')) return;
    await deleteJornada(id); carregar();
  };

  // ── resumo dos dias para a tabela ──────────────────────
  const diasAtivos = j => {
    const dias = j.horarios_dias && Object.keys(j.horarios_dias).length
      ? Object.keys(j.horarios_dias)
      : (j.dias_semana || []);
    if (!dias.length) return '—';
    const idx = DIAS.map((d, i) => dias.includes(d) ? i : -1).filter(i => i >= 0);
    if (idx.length === 5 && JSON.stringify(idx) === '[0,1,2,3,4]') return 'Seg — Sex';
    if (idx.length === 6 && JSON.stringify(idx) === '[0,1,2,3,4,5]') return 'Seg — Sáb';
    if (idx.length === 7) return 'Todos os dias';
    return dias.map(d => DIAS_SHORT[d]).join(', ');
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
            <i className="bi bi-calendar2-week-fill" />
          </div>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Cadastros</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Jornadas de Trabalho</div>
          </div>
        </div>
        <button className="btn-primary-custom" onClick={() => abrir()}>
          <i className="bi bi-plus-lg" />Nova Jornada
        </button>
      </div>

      <div className="filter-bar">
        <div style={{ minWidth: 120 }}>
          <label className="form-label">Status</label>
          <select className="form-select" value={filtros.ativo} onChange={e => setFiltros({ ...filtros, ativo: e.target.value })}>
            <option value="">Todas</option>
            <option value="true">Ativas</option>
            <option value="false">Inativas</option>
          </select>
        </div>
      </div>

      <div className="card-ponto">
        <div className="table-responsive">
          <table className="table-ponto" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 32 }}>Nome da Jornada</th>
                <th>Dias</th>
                <th>Horários</th>
                <th>Carga Média</th>
                <th>Vigência</th>
                <th>Status</th>
                <th style={{ width: 80 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-3">
                  <span className="spinner-border spinner-border-sm text-primary" />
                </td></tr>
              )}
              {!loading && jornadas.map(j => {
                const hd = j.horarios_dias || {};
                const diasList = Object.keys(hd).length ? Object.keys(hd) : (j.dias_semana || []);
                const horarioResumo = (() => {
                  if (!Object.keys(hd).length) {
                    return j.hora_entrada && j.hora_saida ? `${j.hora_entrada} – ${j.hora_saida}` : '—';
                  }
                  const entradas = [...new Set(diasList.map(d => hd[d]?.entrada).filter(Boolean))];
                  const saidas = [...new Set(diasList.map(d => hd[d]?.saida).filter(Boolean))];
                  if (entradas.length === 1 && saidas.length === 1) return `${entradas[0]} – ${saidas[0]}`;
                  return 'Variado';
                })();
                return (
                  <tr key={j.id} style={{ cursor: 'pointer' }} onClick={() => setConsulta(j)}>
                    <td style={{ paddingLeft: 32 }}>
                      <span style={{ background: '#eff6ff', color: 'var(--blue)', borderRadius: 3, padding: '1px 8px', fontSize: 'var(--fs-xs)', fontWeight: 700 }}>
                        {j.nome}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{diasAtivos(j)}</td>
                    <td style={{ fontWeight: 600 }}>{horarioResumo}</td>
                    <td style={{ fontWeight: 700, color: 'var(--blue)' }}>{toHHMM(parseFloat(j.carga_horaria) || 0)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                      {j.vigencia_inicio ? `${j.vigencia_inicio}${j.vigencia_fim ? ' a ' + j.vigencia_fim : ''}` : '—'}
                    </td>
                    <td>
                      <span style={{ background: j.ativo ? '#dcfce7' : '#fee2e2', color: j.ativo ? '#166534' : '#991b1b', borderRadius: 3, padding: '1px 8px', fontSize: 'var(--fs-xs)', fontWeight: 700 }}>
                        {j.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-icon" style={{ color: '#475569', marginRight: 5 }} title="Consultar" onClick={() => setConsulta(j)}>
                        <i className="bi bi-eye" />
                      </button>
                      <button className="btn-icon" style={{ color: '#1d4ed8', marginRight: 5 }} title="Editar" onClick={() => abrir(j)}>
                        <i className="bi bi-pencil" />
                      </button>
                      <button className="btn-icon" style={{ color: '#1d4ed8', marginRight: 5 }} title="Copiar" onClick={() => copiar(j)}>
                        <i className="bi bi-copy" />
                      </button>
                      <button className="btn-icon" style={{ color: '#dc2626' }} title="Excluir" onClick={() => excluir(j.id)}>
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && jornadas.length === 0 && (
                <tr><td colSpan={7}>
                  <div className="empty-state"><i className="bi bi-calendar2-week" /><p>Nenhuma jornada cadastrada</p></div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal-box" style={{ maxWidth: 740 }}>
            <div className="modal-header-custom">
              <h5><i className="bi bi-calendar2-week me-2" />{editId ? 'Editar Jornada' : 'Nova Jornada'}</h5>
              <button className="modal-close" onClick={() => setModal(false)}><i className="bi bi-x-lg" /></button>
            </div>
            <form onSubmit={salvar}>
              <div className="modal-body-custom">
                {erro && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 12px', color: '#991b1b', fontSize: 'var(--fs-xs)', marginBottom: 12 }}>
                    {erro}
                  </div>
                )}
                <div className="row g-2 mb-3">
                  <div className="col-md-8">
                    <label className="form-label">Nome da Jornada *</label>
                    <input className="form-control" required placeholder="Ex: Estágio Seg–Sex, Administrativo 8h..." value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Carga Média</label>
                    <input className="form-control" readOnly value={toHHMM(parseFloat(form.carga_horaria) || 0)} style={{ background: 'var(--gray-50)', fontWeight: 700, color: 'var(--blue)' }} />
                  </div>
                  <div className="col-md-2 d-flex align-items-end pb-1">
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox" id="jornada-ativo" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} />
                      <label className="form-check-label" htmlFor="jornada-ativo" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>Ativa</label>
                    </div>
                  </div>
                </div>

                {/* Tabela de dias */}
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
                  {/* Cabeçalho */}
                  <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 1fr 1fr 70px', background: 'var(--navy)', color: '#fff', fontSize: 'var(--fs-xs)', fontWeight: 700, padding: '6px 10px', gap: 8 }}>
                    <span>DIA</span>
                    <span>ENTRADA</span>
                    <span>INÍC. ALMOÇO</span>
                    <span>FIM ALMOÇO</span>
                    <span>SAÍDA</span>
                    <span style={{ textAlign: 'right' }}>TOTAL</span>
                  </div>

                  {DIAS.map((dia, idx) => {
                    const ativo = isDiaAtivo(dia);
                    const h = form.horarios_dias[dia] || DIA_VAZIO;
                    const cargaDia = ativo ? calcCarga(h) : 0;
                    return (
                      <div key={dia} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 1fr 1fr 70px', gap: 8, padding: '6px 10px', alignItems: 'center', background: idx % 2 === 0 ? '#fff' : 'var(--gray-50)', borderTop: idx > 0 ? '1px solid var(--border)' : 'none' }}>
                        {/* Dia */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', margin: 0, fontWeight: ativo ? 700 : 400, fontSize: 'var(--fs-sm)', color: ativo ? 'var(--navy)' : 'var(--text-muted)' }}>
                          <input type="checkbox" checked={ativo} onChange={() => toggleDia(dia)} style={{ width: 15, height: 15, accentColor: 'var(--navy)' }} />
                          {DIAS_FULL[dia]}
                        </label>
                        {/* Entrada */}
                        <input type="time" className="form-control" disabled={!ativo} value={ativo ? h.entrada : ''} onChange={e => setHorarioDia(dia, 'entrada', e.target.value)} style={{ fontSize: 'var(--fs-sm)', padding: '3px 6px', opacity: ativo ? 1 : 0.35 }} />
                        {/* Início almoço */}
                        <input type="time" className="form-control" disabled={!ativo} value={ativo ? h.inicio_almoco : ''} onChange={e => setHorarioDia(dia, 'inicio_almoco', e.target.value)} style={{ fontSize: 'var(--fs-sm)', padding: '3px 6px', opacity: ativo ? 1 : 0.35 }} />
                        {/* Fim almoço */}
                        <input type="time" className="form-control" disabled={!ativo} value={ativo ? h.fim_almoco : ''} onChange={e => setHorarioDia(dia, 'fim_almoco', e.target.value)} style={{ fontSize: 'var(--fs-sm)', padding: '3px 6px', opacity: ativo ? 1 : 0.35 }} />
                        {/* Saída */}
                        <input type="time" className="form-control" disabled={!ativo} value={ativo ? h.saida : ''} onChange={e => setHorarioDia(dia, 'saida', e.target.value)} style={{ fontSize: 'var(--fs-sm)', padding: '3px 6px', opacity: ativo ? 1 : 0.35 }} />
                        {/* Total do dia */}
                        <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 'var(--fs-sm)', color: cargaDia > 0 ? 'var(--blue)' : 'var(--text-muted)' }}>
                          {ativo && cargaDia > 0 ? toHHMM(cargaDia) : '—'}
                        </div>
                      </div>
                    );
                  })}

                  {/* Rodapé — totais */}
                  {(() => {
                    const dias = Object.keys(form.horarios_dias);
                    if (!dias.length) return null;
                    const totalSemana = dias.reduce((s, d) => s + calcCarga(form.horarios_dias[d]), 0);
                    const mediaDia = totalSemana / dias.length;
                    return (
                      <div style={{ padding: '7px 10px', background: '#f0f4ff', borderTop: '2px solid var(--blue)', display: 'flex', gap: 24, alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--navy)' }}>
                        <span>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Dias ativos: </span>
                          <strong>{dias.length}</strong>
                        </span>
                        <span>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Média diária: </span>
                          <strong style={{ color: 'var(--blue)' }}>{toHHMM(mediaDia)}</strong>
                        </span>
                        <span>
                          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Total semana: </span>
                          <strong style={{ color: 'var(--navy)', fontSize: 'var(--fs-sm)' }}>{toHHMM(totalSemana)}</strong>
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Ação replicar */}
                {Object.keys(form.horarios_dias).length > 1 && (
                  <button type="button" className="btn-secondary-custom" onClick={copiarPrimeiro}>
                    <i className="bi bi-copy" />Replicar horário do 1º dia para todos
                  </button>
                )}

                {/* Vigência */}
                <div className="row g-2 mt-2">
                  <div className="col-md-4">
                    <label className="form-label">Vigência Início</label>
                    <input type="date" className="form-control" value={form.vigencia_inicio} onChange={e => setForm({ ...form, vigencia_inicio: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Vigência Fim</label>
                    <input type="date" className="form-control" value={form.vigencia_fim} onChange={e => setForm({ ...form, vigencia_fim: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="modal-footer-custom">
                <button type="button" className="btn-secondary-custom" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary-custom" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg" />Salvar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Consulta */}
      {consulta && (() => {
        const hd = consulta.horarios_dias || {};
        const diasAtivosConsulta = DIAS.filter(d => hd[d]);
        const totalSemana = diasAtivosConsulta.reduce((s, d) => s + calcCarga(hd[d]), 0);
        return (
          <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setConsulta(null)}>
            <div className="modal-box" style={{ maxWidth: 740 }}>
              <div className="modal-header-custom">
                <h5><i className="bi bi-eye me-2" />Consultar Jornada</h5>
                <button className="modal-close" onClick={() => setConsulta(null)}><i className="bi bi-x-lg" /></button>
              </div>
              <div className="modal-body-custom">
                {/* Cabeçalho info */}
                <div className="row g-2 mb-3">
                  <div className="col-md-8">
                    <label className="form-label">Nome da Jornada</label>
                    <div className="form-control" style={{ background: 'var(--gray-50)', fontWeight: 700 }}>{consulta.nome}</div>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Carga Média</label>
                    <div className="form-control" style={{ background: 'var(--gray-50)', fontWeight: 700, color: 'var(--blue)', textAlign: 'center' }}>
                      {toHHMM(parseFloat(consulta.carga_horaria) || 0)}
                    </div>
                  </div>
                  <div className="col-md-2 d-flex align-items-end pb-1">
                    <span style={{ background: consulta.ativo ? '#dcfce7' : '#fee2e2', color: consulta.ativo ? '#166534' : '#991b1b', borderRadius: 4, padding: '4px 12px', fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
                      {consulta.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                </div>

                {/* Tabela de horários */}
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 1fr 1fr 70px', background: 'var(--navy)', color: '#fff', fontSize: 'var(--fs-xs)', fontWeight: 700, padding: '6px 10px', gap: 8 }}>
                    <span>DIA</span>
                    <span>ENTRADA</span>
                    <span>INÍC. ALMOÇO</span>
                    <span>FIM ALMOÇO</span>
                    <span>SAÍDA</span>
                    <span style={{ textAlign: 'right' }}>TOTAL</span>
                  </div>
                  {DIAS.map((dia, idx) => {
                    const ativo = !!hd[dia];
                    const h = hd[dia] || DIA_VAZIO;
                    const cargaDia = ativo ? calcCarga(h) : 0;
                    const cell = (val) => (
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: ativo ? 600 : 400, color: ativo ? 'var(--navy)' : 'var(--text-muted)', opacity: ativo ? 1 : 0.4 }}>
                        {ativo && val ? val : '—'}
                      </div>
                    );
                    return (
                      <div key={dia} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 1fr 1fr 70px', gap: 8, padding: '6px 10px', alignItems: 'center', background: idx % 2 === 0 ? '#fff' : 'var(--gray-50)', borderTop: idx > 0 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: ativo ? 700 : 400, fontSize: 'var(--fs-sm)', color: ativo ? 'var(--navy)' : 'var(--text-muted)' }}>
                          <i className={`bi ${ativo ? 'bi-check-circle-fill text-success' : 'bi-circle'}`} style={{ fontSize: 'var(--fs-base)' }} />
                          {DIAS_FULL[dia]}
                        </div>
                        {cell(h.entrada)}
                        {cell(h.inicio_almoco)}
                        {cell(h.fim_almoco)}
                        {cell(h.saida)}
                        <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 'var(--fs-sm)', color: cargaDia > 0 ? 'var(--blue)' : 'var(--text-muted)' }}>
                          {ativo && cargaDia > 0 ? toHHMM(cargaDia) : '—'}
                        </div>
                      </div>
                    );
                  })}
                  {diasAtivosConsulta.length > 0 && (
                    <div style={{ padding: '7px 10px', background: '#f0f4ff', borderTop: '2px solid var(--blue)', display: 'flex', gap: 24, alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--navy)' }}>
                      <span><span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Dias ativos: </span><strong>{diasAtivosConsulta.length}</strong></span>
                      <span><span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Média diária: </span><strong style={{ color: 'var(--blue)' }}>{toHHMM(totalSemana / diasAtivosConsulta.length)}</strong></span>
                      <span><span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Total semana: </span><strong style={{ color: 'var(--navy)', fontSize: 'var(--fs-sm)' }}>{toHHMM(totalSemana)}</strong></span>
                    </div>
                  )}
                </div>

                {/* Vigência */}
                {(consulta.vigencia_inicio || consulta.vigencia_fim) && (
                  <div className="row g-2">
                    <div className="col-md-4">
                      <label className="form-label">Vigência Início</label>
                      <div className="form-control" style={{ background: 'var(--gray-50)' }}>{consulta.vigencia_inicio || '—'}</div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Vigência Fim</label>
                      <div className="form-control" style={{ background: 'var(--gray-50)' }}>{consulta.vigencia_fim || '—'}</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer-custom">
                <button type="button" className="btn-secondary-custom" onClick={() => setConsulta(null)}>Fechar</button>
                <button type="button" className="btn-primary-custom" onClick={() => { setConsulta(null); abrir(consulta); }}>
                  <i className="bi bi-pencil me-1" />Editar
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
